<template>
  <div class="overflow-hidden">
    <label
      for="drawer"
      style="transition-timing-function: ease;"
      class="peer bg-black/40 fixed inset-0 z-[9999] !m-0 transition-opacity duration-300 opacity-0 pointer-events-none has-[:checked]:opacity-100 has-[:checked]:pointer-events-auto"
    >
      <input
        id="drawer"
        type="checkbox"
        class="hidden"
      >
    </label>
    <div
      style="transition-timing-function: ease;"
      class="translate-x-full peer-has-[:checked]:translate-x-0 transition-transform duration-300 will-change-transform py-[1.5rem] bg-white !border border-[#E2E2E2] w-full max-w-[600px] fixed right-0 inset-y-0 z-[10000] flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center gap-[16px] px-[1.5rem] pb-[1.5rem] !border-b border-[#E2E2E2] shrink-0">
        <button
          class="w-[40px] h-[32px] button-primary !grid place-items-center"
          @click="handleBack"
        >
          <span class="k-arrow-left-2 text-[20px] text-white" />
        </button>
        <span class="text-[20px] text-[#333] font-semibold flex-1">Detail Kreator</span>
        <button
          v-if="!isEditMode && !error && !loading"
          class="flex items-center gap-[6px] px-[12px] h-[32px] border border-[#E2E2E2] rounded-[8px] text-[14px] text-[#333] bg-white hover:bg-gray-50"
          @click="enterEditMode"
        >
          <span class="k-edit text-[16px]" />
          Edit Kreator
        </button>
      </div>

      <!-- Loading -->
      <div
        v-if="loading"
        class="flex items-center justify-center gap-[8px] flex-1"
      >
        <b-spinner
          variant="primary"
          small
        />
        <span class="text-[#828282]">memuat detail kreator...</span>
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="px-[1.5rem] pt-[1rem]"
      >
        <b-alert
          variant="danger"
          show
        >
          {{ error.message }}
        </b-alert>
      </div>

      <!-- Detail Mode -->
      <div
        v-else-if="creatorDetail && !isEditMode"
        class="px-[1.5rem] flex-1 overflow-y-auto"
      >
        <div
          v-for="(detail, label) in creatorDetail"
          :key="label"
          class="py-[16px] flex flex-col gap-[16px] text-[14px] !border-b border-dashed border-[#E2E2E2] last:!border-none"
        >
          <template v-if="Object.keys(detail).length">
            <span class="text-[#333]">{{ label }}</span>
            <div
              v-for="(value, key) in detail"
              :key="key"
              class="text-[#828282] flex flex-col gap-[12px]"
            >
              <div class="flex gap-[16px]">
                <span class="min-w-[128px] max-w-[128px] sm:min-w-[150px] sm:max-w-[150px]">{{ key }}</span>
                <span class="text-[#222]">:</span>
                <a
                  v-if="(key === 'Link Sosmed' || key === 'TikTok') && value && value !== '-'"
                  :href="safeUrl(value)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block truncate text-[#08A0F7]"
                >{{ formatSocialDisplay(value) }}</a>
                <span
                  v-else
                  :class="{'capitalize': !['Email', 'No HP', 'No. HP', 'Usia'].includes(key)}"
                >{{ value }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Edit Mode -->
      <div
        v-else-if="editForm && isEditMode"
        class="member-drawer-edit flex flex-col flex-1 overflow-hidden"
      >
        <div class="px-[1.5rem] flex-1 overflow-y-auto">
          <div
            v-for="(section, sectionLabel) in editForm"
            :key="sectionLabel"
            class="py-[16px] flex flex-col gap-[16px] text-[14px] !border-b border-dashed border-[#E2E2E2] last:!border-none"
          >
            <span class="text-[#333] font-medium">{{ sectionLabel }}</span>
            <div
              v-for="field in section"
              :key="field.key"
              class="flex flex-col gap-[6px]"
            >
              <label class="text-[12px] text-[#828282]">
                {{ field.label }}
                <span
                  v-if="field.required"
                  class="text-red-500 ml-[2px]"
                >*</span>
              </label>

              <!-- Read-only -->
              <div
                v-if="field.readonly"
                class="min-h-[38px] px-[12px] py-[8px] flex items-center rounded-[8px] bg-[#F4F4F4] border border-[#E2E2E2] text-[14px] text-[#828282]"
              >
                {{ field.value }}
              </div>

              <!-- No HP — input dengan prefix +62 -->
              <b-input-group
                v-else-if="field.inputType === 'tel' || field.key === 'phone'"
                :class="{ 'is-invalid-group': field.error }"
              >
                <b-input-group-prepend is-text>
                  +62
                </b-input-group-prepend>
                <b-form-input
                  v-model="field.value"
                  type="tel"
                  :formatter="formatPhone"
                  :state="field.error ? false : null"
                  class="rounded-r-[8px]"
                  placeholder="8xx-xxxx-xxxx"
                  @keypress="onKeyPressNumber"
                  @input="onFieldInput(field, $event)"
                />
              </b-input-group>

              <!-- Kategori — multi-select -->
              <v-select
                v-else-if="field.inputType === 'category' || field.key === 'category'"
                v-model="field.value"
                :options="categoryOptions"
                label="label"
                multiple
                :clearable="false"
                placeholder="Pilih kategori..."
                class="custom-v-select bg-white"
                :class="{ 'is-invalid': field.error }"
                @input="onFieldInput(field, $event)"
              />

              <!-- Domisili — searchable select with debounce 1s -->
              <v-select
                v-else-if="field.inputType === 'domicile' || field.key === 'domicile'"
                v-model="field.value"
                :options="cityOptions"
                :reduce="opt => (typeof opt === 'object' ? (opt.value || opt.label) : opt)"
                :filterable="false"
                :clearable="false"
                placeholder="Pilih atau cari kota/kabupaten..."
                class="custom-v-select bg-white"
                :class="{ 'is-invalid': field.error }"
                @search="onSearchCity"
                @input="onFieldInput(field, $event)"
              >
                <template #no-options="{ search, searching }">
                  <template v-if="searching">
                    Lokasi untuk <em>'{{ search }}'</em> tidak ditemukan.
                  </template>
                  <em
                    v-else
                    class="text-[#828282]"
                  >Ketik nama kota untuk mencari</em>
                </template>
              </v-select>

              <!-- Dropdown single select -->
              <v-select
                v-else-if="field.inputType === 'select'"
                v-model="field.value"
                :options="field.options"
                :clearable="false"
                placeholder="Pilih..."
                class="custom-v-select bg-white"
                :class="{ 'is-invalid': field.error }"
                @input="onFieldInput(field, $event)"
              />

              <!-- Text / number / email input biasa -->
              <b-form-input
                v-else
                v-model="field.value"
                :type="field.inputType || 'text'"
                size="md"
                :state="field.error ? false : null"
                class="rounded-[8px]"
                @keypress="field.noNumbers ? onKeyPressLetterOnly($event) : undefined"
                @input="onFieldInput(field, $event)"
              />

              <span
                v-if="field.error"
                class="text-[11px] text-red-500"
              >{{ field.error }}</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex gap-[12px] px-[1.5rem] py-[1rem] !border-t border-[#E2E2E2] shrink-0">
          <button
            class="flex-1 h-[40px] rounded-[8px] border border-[#E2E2E2] text-[14px] text-[#333] bg-white hover:bg-gray-50"
            :disabled="loadingSave"
            @click="cancelEdit"
          >
            Batalkan
          </button>
          <button
            class="flex-1 h-[40px] rounded-[8px] button-primary text-[14px] text-white disabled:opacity-60"
            :disabled="isSaveDisabled"
            @click="saveEdit"
          >
            <b-spinner
              v-if="loadingSave"
              small
              variant="light"
            />
            <span v-else>Simpan Perubahan</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { DAY_MONTH_YEAR } from '@/libs/filterDate'
import { komtimAxiosIns } from '@/libs/axios'
import { parseAge, parsePhone, parseStatus } from '@/views/pages/komlabs/creator-management/utils'

function findAnswer(answers, matchLabels) {
  if (!Array.isArray(answers)) return null
  return answers.find(a => matchLabels.some(l => l.toLowerCase() === (a.label || '').trim().toLowerCase()))
}

const TIKTOK_USERNAME_RE = /^(?:(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@*([a-zA-Z0-9_.-]+)(?:[/?#].*)?|@*([a-zA-Z0-9_.-]+))$/i

function extractTikTokUsername(input) {
  if (!input || input === '-') return ''
  const match = String(input).trim().match(TIKTOK_USERNAME_RE)
  return match ? (match[1] || match[2] || '') : ''
}

function formatTikTokUrl(input) {
  if (!input || input === '-') return ''
  const trimmed = String(input).trim()
  const username = extractTikTokUsername(trimmed)
  return username ? `https://www.tiktok.com/@${username}` : trimmed
}

function formatSocialLinkDetail(link) {
  return formatTikTokUrl(link) || '-'
}

function validateField(field) {
  const val = field.value

  if (field.required) {
    const isEmpty = val === null || val === undefined || val === ''
      || (Array.isArray(val) && val.length === 0)
    if (isEmpty) return `${field.label} wajib diisi`
  }

  if (!val && !field.required) return null

  if (field.inputType === 'email' || field.key === 'email') {
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRe.test(val)) return 'Format email tidak valid'
  }

  if (field.inputType === 'tel' || field.key === 'phone') {
    const strVal = String(val || '').trim()
    if (!strVal.startsWith('8')) {
      return 'nomor telepon harus diawali dengan angka "8"'
    }
    if (strVal.length < 9) {
      return 'panjang nomor telepon minimal adalah 9 angka'
    }
    if (strVal.length > 14) {
      return 'panjang nomor telepon maksimal adalah 14 angka'
    }
  }

  return null
}

function getFormSnapshot(form) {
  if (!form) return null
  const fields = Object.values(form).flat()
  const snapshot = {}
  fields.forEach(f => {
    if (f.readonly) return
    if (f.key === 'category') {
      const arr = Array.isArray(f.value)
        ? f.value.map(v => (typeof v === 'object' && v !== null ? (v.value || v.label) : v)).filter(Boolean).sort()
        : [f.value].filter(Boolean)
      snapshot[f.key] = JSON.stringify(arr)
    } else if (f.key === 'domicile') {
      const str = typeof f.value === 'object' && f.value !== null ? (f.value.value || f.value.label || '') : (f.value || '')
      snapshot[f.key] = str
    } else {
      snapshot[f.key] = f.value ?? ''
    }
  })
  return snapshot
}

export default {
  name: 'MemberDrawer',
  components: { vSelect },
  emits: ['saved'],
  data() {
    return {
      creatorDetail: null,
      editForm: null,
      initialFormSnapshot: null,
      isEditMode: false,
      loading: false,
      loadingSave: false,
      error: null,
      rawItem: null,
      currentId: null,
      currentMapper: null,
      categoryOptions: [],
      cityOptions: [],
      cityDebounceTimeout: null,
    }
  },
  computed: {
    isFormChanged() {
      if (!this.editForm || !this.initialFormSnapshot) return false
      const currentSnapshot = getFormSnapshot(this.editForm)
      return Object.keys(this.initialFormSnapshot).some(
        key => String(this.initialFormSnapshot[key]) !== String(currentSnapshot[key]),
      )
    },

    hasFormError() {
      if (!this.editForm) return false
      const allFields = Object.values(this.editForm).flat()
      return allFields.some(field => {
        if (field.readonly) return false
        if (field.error) return true
        return Boolean(validateField(field))
      })
    },

    isSaveDisabled() {
      return this.loadingSave || !this.isFormChanged || this.hasFormError
    },
  },
  methods: {
    formatPhone(value) {
      return String(value || '').replace(/\D/g, '')
    },

    onKeyPressNumber(event) {
      const char = String.fromCharCode(event.which || event.keyCode)
      if (!/[0-9]/.test(char)) {
        event.preventDefault()
      }
    },

    safeUrl(url) {
      if (!url || url === '-') return '#'
      const formatted = formatTikTokUrl(url)
      if (/^(javascript|data|vbscript):/i.test(formatted)) return '#'
      return /^https?:\/\//i.test(formatted) ? formatted : `https://${formatted}`
    },

    formatSocialDisplay(value) {
      if (!value || value === '-') return '-'
      return formatTikTokUrl(value) || value
    },

    closeDrawer() {
      document.getElementById('drawer').click()
    },

    openDrawer(id, mapper) {
      const defaultMapper = data => ({
        'Data Diri': {
          Nama: data.name || '-',
          Email: data.email || '-',
          'No HP': data.phone ? parsePhone(data.phone) : '-',
          'Link Sosmed': formatSocialLinkDetail(data.social_link || '-'),
          Kategori: Array.isArray(data.category) ? data.category.join(', ') : (data.category || '-'),
          'Tanggal Register': data.register_date ? DAY_MONTH_YEAR(data.register_date) : '-',
        },
        'Lain-lain': {
          'GMV 30 hari terakhir': data.gmv_30_days ?? '-',
          'Jumlah Followers': data.followers_count ?? '-',
          Domisili: data.domicile || data.city || '-',
          Gender: data.gender || '-',
          Usia: parseAge(data.age),
          'Tertarik menjadi anggota MCN Komlabs?': data.mcn_interest || '-',
          'Kreator Status': parseStatus(data.status),
          ...(data.content_type ? { 'Tipe Konten': data.content_type } : {}),
          ...(data.other_platform ? { 'Platform Lainnya': data.other_platform } : {}),
        },
      })

      this.isEditMode = false
      this.editForm = null
      this.initialFormSnapshot = null
      this.error = null
      this.creatorDetail = null
      this.rawItem = null
      this.currentId = id
      this.currentMapper = mapper || defaultMapper
      this.categoryOptions = []
      this.cityOptions = []
      this.fetchCreatorDetail(id, this.currentMapper)
      document.getElementById('drawer').click()
    },

    async fetchCreatorDetail(id, mapper) {
      if (!id) return
      this.loading = true
      try {
        const { data: { data } } = await this.$http_new.get(`/komclass/api/v1/komlabs/creators/${id}`)
        const mapped = mapper(data)
        if (mapped) {
          const formattedSocial = formatSocialLinkDetail(data.social_link || '-')
          Object.keys(mapped).forEach(sectionKey => {
            const section = mapped[sectionKey]
            if (section && typeof section === 'object' && 'Link Sosmed' in section) {
              mapped[sectionKey] = {
                ...section,
                'Link Sosmed': formattedSocial,
              }
            }
          })
        }
        this.creatorDetail = mapped
        this.rawItem = data
      } catch (err) {
        this.error = err
      } finally {
        this.loading = false
      }
    },

    async fetchCategoryOptions() {
      if (this.categoryOptions.length) return
      try {
        const { data } = await this.$http_new.get('/komclass/api/v1/komlabs/campaigns/options/product-category')
        const list = data?.data || data || []
        this.categoryOptions = list
          .filter(item => item.is_active === 1 || item.is_active === true)
          .map(item => ({
            label: item.option_label || item.label || item.name || '',
            value: item.option_label || item.option_value || item.label || '',
          }))
      } catch {
        // tidak blokir edit
      }
    },

    onSearchCity(search, loading) {
      if (!search || !search.trim()) return
      if (this.cityDebounceTimeout) {
        clearTimeout(this.cityDebounceTimeout)
      }
      loading(true)
      this.cityDebounceTimeout = setTimeout(async () => {
        try {
          await this.fetchCityOptions(search.trim())
        } finally {
          loading(false)
        }
      }, 1000)
    },

    async fetchCityOptions(search) {
      if (!search) return
      try {
        const { data } = await komtimAxiosIns.get('v1/region/villages', {
          params: {
            offset: 0,
            limit: 20,
            keyword: search,
          },
        })
        const list = data?.data || data || []
        const mapped = list.map(item => {
          const parts = [
            item.subdistrict_name,
            item.district_name,
            item.regency_name,
            item.province_name,
          ].filter(Boolean)
          const label = parts.length ? parts.join(', ') : (item.name || item.label || '')
          return { label, value: label }
        })
        const currentDom = this.editForm?.['Lain-lain']?.find(f => f.key === 'domicile')?.value
        if (currentDom) {
          const strVal = typeof currentDom === 'object' ? (currentDom.value || currentDom.label) : currentDom
          if (strVal && !mapped.some(o => o.value === strVal)) {
            mapped.unshift({ label: strVal, value: strVal })
          }
        }
        this.cityOptions = mapped
      } catch {
        // ignore
      }
    },

    handleBack() {
      if (this.isEditMode) {
        this.cancelEdit()
      } else {
        this.closeDrawer()
      }
    },

    async enterEditMode() {
      if (this.currentId) {
        await this.fetchCreatorDetail(this.currentId, this.currentMapper)
      }

      await this.fetchCategoryOptions()

      let existingCategories = []
      const categoryAnswer = findAnswer(this.rawItem?.answers, ['Kategori', 'Category'])
      const rawCategory = this.rawItem?.category || categoryAnswer?.answer

      if (Array.isArray(rawCategory)) {
        existingCategories = rawCategory
      } else if (typeof rawCategory === 'string' && rawCategory) {
        existingCategories = rawCategory.split(',').map(v => v.trim()).filter(Boolean)
      }
      existingCategories.forEach(val => {
        if (!this.categoryOptions.some(o => o.value === val || o.label === val)) {
          this.categoryOptions.push({ label: val, value: val })
        }
      })

      const domAnswer = findAnswer(this.rawItem?.answers, ['Domisili', 'Asal Kota', 'City'])
      const domVal = this.rawItem?.domicile || this.rawItem?.city || domAnswer?.answer || ''
      if (domVal) {
        this.cityOptions = [{ label: domVal, value: domVal }]
      } else {
        this.cityOptions = []
      }

      this.editForm = this.buildEditForm(this.rawItem)
      this.initialFormSnapshot = getFormSnapshot(this.editForm)
      this.isEditMode = true
    },

    cancelEdit() {
      this.isEditMode = false
      this.editForm = null
      this.initialFormSnapshot = null
    },

    buildEditForm(item) {
      if (!item) return null

      const answers = item.answers || []

      // Extract existing categories
      let existingCategoryLabels = []
      const catAnswer = findAnswer(answers, ['Kategori', 'Category'])
      const rawCategory = item.category || catAnswer?.answer
      if (Array.isArray(rawCategory)) {
        existingCategoryLabels = rawCategory
      } else if (typeof rawCategory === 'string' && rawCategory) {
        existingCategoryLabels = rawCategory.split(',').map(v => v.trim()).filter(Boolean)
      }
      const categoryValue = existingCategoryLabels.map(v => ({ label: v, value: v }))

      // Phone display (strip leading 0 or 62)
      const phoneAnswer = findAnswer(answers, ['No HP', 'No. HP', 'Nomor HP', 'Phone'])
      const rawPhone = item.phone || phoneAnswer?.answer || ''
      const phoneDisplay = String(rawPhone).replace(/\s|-/g, '').replace(/^(\+?62|0)/, '')

      // Social Link display (tampilkan username jika TikTok, pertahankan URL jika non-TikTok)
      const socialAnswer = findAnswer(answers, ['Link Sosmed'])
      const rawSocial = item.social_link || socialAnswer?.answer || ''
      const socialDisplay = extractTikTokUsername(rawSocial) || rawSocial

      // Name
      const nameAnswer = findAnswer(answers, ['Nama', 'Nama Lengkap'])
      const nameVal = item.name || nameAnswer?.answer || ''

      // Email
      const emailAnswer = findAnswer(answers, ['Email'])
      const emailVal = item.email || emailAnswer?.answer || ''

      // Register date
      const regDateVal = item.register_date ? DAY_MONTH_YEAR(item.register_date) : '-'

      // GMV
      const gmvAnswer = findAnswer(answers, ['GMV 30 hari terakhir', 'GMV 30 Hari Terakhir', 'GMV'])
      const gmvVal = item.gmv_30_days ?? (gmvAnswer?.answer !== undefined && gmvAnswer?.answer !== null ? Number(gmvAnswer.answer) : '')

      // Followers
      const followersAnswer = findAnswer(answers, ['Jumlah Followers', 'Followers'])
      const followersVal = item.followers_count ?? (followersAnswer?.answer !== undefined && followersAnswer?.answer !== null ? Number(followersAnswer.answer) : '')

      // Domicile / City
      const domAnswer = findAnswer(answers, ['Domisili', 'Asal Kota', 'City'])
      const domVal = item.domicile || item.city || domAnswer?.answer || ''

      // Age
      const ageAnswer = findAnswer(answers, ['Usia', 'Age'])
      const ageVal = item.age ? parseAge(item.age) : (item.dob || ageAnswer?.answer || '-')

      // MCN Interest
      const mcnAnswer = findAnswer(answers, ['Apakah sudah join dengan MCN affiliate lain?', 'Tertarik menjadi anggota MCN Komlabs?', 'Tertarik MCN?'])
      const mcnVal = item.mcn_interest || mcnAnswer?.answer || ''

      // Creator Status
      const statusVal = parseStatus(item.status)

      // Other Platform (optional)
      const otherAnswer = findAnswer(answers, ['Platform Lainnya'])
      const otherVal = item.other_platform || otherAnswer?.answer || ''

      const dataDiriFields = [
        {
          key: 'name',
          label: 'Nama',
          value: nameVal,
          required: true,
          inputType: 'text',
          noNumbers: true,
          answerId: nameAnswer?.id,
          questionId: nameAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'email',
          label: 'Email',
          value: emailVal,
          required: true,
          inputType: 'email',
          answerId: emailAnswer?.id,
          questionId: emailAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'phone',
          label: 'No HP',
          value: phoneDisplay,
          required: true,
          inputType: 'tel',
          answerId: phoneAnswer?.id,
          questionId: phoneAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'social_link',
          label: 'Link Sosmed',
          value: socialDisplay,
          required: false,
          inputType: 'text',
          answerId: socialAnswer?.id,
          questionId: socialAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'category',
          label: 'Kategori',
          value: categoryValue,
          required: true,
          inputType: 'category',
          answerId: catAnswer?.id,
          questionId: catAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'register_date',
          label: 'Tanggal Register',
          value: regDateVal,
          required: false,
          inputType: null,
          readonly: true,
          error: null,
        },
      ]

      const lainLainFields = [
        {
          key: 'gmv_30_days',
          label: 'GMV 30 hari terakhir',
          value: gmvVal,
          required: false,
          inputType: 'number',
          answerId: gmvAnswer?.id,
          questionId: gmvAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'followers_count',
          label: 'Jumlah Followers',
          value: followersVal,
          required: false,
          inputType: 'number',
          answerId: followersAnswer?.id,
          questionId: followersAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'domicile',
          label: 'Domisili',
          value: domVal,
          required: false,
          inputType: 'domicile',
          answerId: domAnswer?.id,
          questionId: domAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'age',
          label: 'Usia',
          value: ageVal,
          required: false,
          inputType: null,
          readonly: true,
          error: null,
        },
        {
          key: 'mcn_interest',
          label: 'Tertarik menjadi anggota MCN Komlabs?',
          value: mcnVal,
          required: false,
          inputType: 'select',
          options: ['Belum', 'Tertarik'],
          answerId: mcnAnswer?.id,
          questionId: mcnAnswer?.question_id,
          readonly: false,
          error: null,
        },
        {
          key: 'status',
          label: 'Kreator Status',
          value: statusVal,
          required: false,
          inputType: null,
          readonly: true,
          error: null,
        },
      ]

      if (otherVal || otherAnswer) {
        lainLainFields.push({
          key: 'other_platform',
          label: 'Platform Lainnya',
          value: otherVal,
          required: false,
          inputType: 'select',
          options: ['Shopee'],
          answerId: otherAnswer?.id,
          questionId: otherAnswer?.question_id,
          readonly: false,
          error: null,
        })
      }

      return {
        'Data Diri': dataDiriFields,
        'Lain-lain': lainLainFields,
      }
    },

    validateForm() {
      let isValid = true
      const allFields = Object.values(this.editForm).flat()
      allFields.forEach(field => {
        if (field.readonly) return
        const err = validateField(field)
        this.$set(field, 'error', err)
        if (err) isValid = false
      })
      return isValid
    },

    buildPayload(editForm) {
      const allFields = Object.values(editForm).flat()
      const payload = {}

      allFields.forEach(field => {
        if (field.readonly) return
        const { value, key } = field

        if (key === 'category') {
          payload.category = Array.isArray(value)
            ? value.map(v => (typeof v === 'object' ? (v.value || v.label) : v)).filter(Boolean).join(',')
            : (value || '')
        } else if (key === 'phone') {
          const stripped = String(value || '').replace(/\s|-/g, '').replace(/^(\+?62|0)/, '')
          payload.phone = `0${stripped}`
        } else if (key === 'social_link') {
          payload.social_link = formatTikTokUrl(value)
        } else if (key === 'followers_count') {
          payload.followers_count = value !== '' && value !== null && value !== undefined ? Number(value) : 0
        } else if (key === 'gmv_30_days') {
          payload.gmv_30_days = value !== '' && value !== null && value !== undefined ? Number(value) : 0
        } else if (key === 'domicile') {
          payload.domisili = value ?? ''
        } else if (key === 'name') {
          payload.name = value ?? ''
        } else if (key === 'email') {
          payload.email = value ?? ''
        } else if (key === 'mcn_interest') {
          payload.mcn_interest = value ?? ''
        } else if (key === 'other_platform') {
          payload.other_platform = value ?? ''
        }
      })

      return payload
    },

    async saveEdit() {
      if (!this.currentId) return
      if (!this.validateForm()) return

      this.loadingSave = true
      try {
        const payload = this.buildPayload(this.editForm)
        await this.$http_new.put(`/komclass/api/v1/komlabs/creators/${this.currentId}`, payload)

        this.$toast_success({ message: 'Data kreator berhasil diperbarui.' })
        this.isEditMode = false
        this.editForm = null
        this.initialFormSnapshot = null
        this.closeDrawer()
        this.$emit('saved')
      } catch (err) {
        const msg = err?.response?.data?.meta?.message || err?.response?.data?.message || ''
        if (msg.toLowerCase().includes('email')) {
          const emailField = Object.values(this.editForm).flat().find(f => f.key === 'email' || f.label === 'Email')
          if (emailField) {
            this.$set(emailField, 'error', 'Email sudah digunakan oleh kreator lain')
          }
          return
        }
        if (msg.toLowerCase().includes('phone') || msg.toLowerCase().includes('nomor') || msg.toLowerCase().includes('telepon') || msg.toLowerCase().includes('no hp')) {
          const phoneField = Object.values(this.editForm).flat().find(f => f.key === 'phone' || f.label === 'No HP' || f.label === 'No. HP')
          if (phoneField) {
            this.$set(phoneField, 'error', msg || 'Nomor HP sudah digunakan oleh kreator lain')
          }
          return
        }
        this.$toast_error({ message: msg || 'Gagal menyimpan perubahan. Silakan coba lagi.' })
      } finally {
        this.loadingSave = false
      }
    },

    onFieldInput(field, value) {
      if (!field || field.readonly) return
      if (value !== undefined) {
        // eslint-disable-next-line no-param-reassign
        field.value = value
      }
      this.$set(field, 'error', validateField(field))
    },

    onPhoneInput(field, value) {
      this.onFieldInput(field, value)
    },

    onKeyPressLetterOnly(event) {
      const char = String.fromCharCode(event.charCode)
      if (!/[a-zA-Z\s]/.test(char)) {
        event.preventDefault()
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.member-drawer-edit .custom-v-select .vs__dropdown-toggle {
  padding: 8px 14px !important;
  border-radius: 8px;
  border-color: #E2E2E2;
  background: #fff;

  .vs__selected-options {
    padding: 0;
    gap: 4px;
  }

  .vs__search,
  .vs__selected {
    padding: 0;
    margin: 0;
  }
}

.member-drawer-edit .custom-v-select.vs--multiple .vs__dropdown-toggle {
  padding: 6px 8px !important;
  gap: 4px;

  .vs__selected-options {
    padding: 2px 0;
    gap: 4px;
    flex-wrap: wrap;
  }

  .vs__selected {
    margin: 2px 3px;
    padding: 2px 8px;
  }
}

.member-drawer-edit .is-invalid .vs__dropdown-toggle {
  border-color: #ea5455 !important;
}

.member-drawer-edit .is-invalid-group .input-group-text {
  border-color: #ea5455;
}
</style>
